import { useNavigate } from 'react-router-dom';
import { CaretRightIcon } from '@phosphor-icons/react';

export default function TestCard({ test, packId }) {
  const navigate = useNavigate();

  const getAbbreviation = (title) => {
    if (title.includes('QPT')) return 'QPT';
    if (title.includes('QFT')) return 'QFT';
    if (title.includes('PYQ')) return 'PYQ';
    if (title.includes('2025')) return '2025';
    if (title.includes('2024')) return '2024';
    if (title.includes('2023')) return '2023';
    return title
      .split(' ')
      .slice(0, 3)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  };

  const handleCardClick = () => {
    if (packId !== '676e96bfe0722dfa6ce1d7ed') {
      navigate(`/v3/${packId}/tests?categoryId=${test.title}`);
    }
  };

  const abbreviation = test.abbreviation || getAbbreviation(test.title);
  const count = test.count || test.testsCount;
  const title = test.title;
  // const description = test.description || `Test series with ${count} tests`;

  const laptopStyles = (
    <style jsx>{`
      @media (min-width: 769px) {
        .laptop\\:mt-1 {
          margin-top: 2px;
        }
        .laptop\\:space-y-1 > * + * {
          margin-top: 3px;
        }
        .laptop\\:leading-tight {
          line-height: 1.25;
        }
      }
      /* Lighter gold base in light mode - the dark-mode tone (#7a5c12) read as muddy/too heavy
         against a light page, so light mode gets a brighter goldenrod base instead. Same bright
         highlight arc (#fff6dc/#f7d98b/#e9b54e) in both themes - that part already reads fine on
         either background. */
      .c99h-hl-glow {
        background: conic-gradient(
          from 0deg at 50% 50%,
          #c9a227 0%,
          #c9a227 62%,
          #fff6dc 75%,
          #f7d98b 82%,
          #e9b54e 88%,
          #c9a227 100%
        );
      }
      .dark .c99h-hl-glow {
        background: conic-gradient(
          from 0deg at 50% 50%,
          #7a5c12 0%,
          #7a5c12 62%,
          #fff6dc 75%,
          #f7d98b 82%,
          #e9b54e 88%,
          #7a5c12 100%
        );
      }
    `}</style>
  );

  const cardBody = (
    <>
      <CaretRightIcon size={16} className="absolute top-4 right-4 text-[var(--q3-neutral-default)]" />

      {/* Main Content */}
      <div
        className="
          flex flex-row items-center gap-3
          sm:flex-row sm:items-center sm:gap-3
          md:flex-col md:items-start md:gap-2
        "
      >
        {/* Icon */}
        {test.icon && (
          <div className="flex items-center justify-center rounded-[48px] w-10 h-10 md:bg-[var(--q3-surface-default)]">
            <img src={test.icon} alt={title} className="w-5 h-5 object-contain" />
          </div>
        )}

        {/* Title + count */}
        <div className="flex flex-col ">
          <p className="text-lg font-bold leading-none laptop:leading-tight mt-2">{title}</p>
          <span className="text-[var(--q3-neutral-light)] text-xs mt-2">{count} Tests</span>
        </div>
      </div>

      {/*
      <p className="text-xs font-light text-[var(--q3-neutral-light)] laptop:mt-2">{description}</p>
      */}

      {laptopStyles}
    </>
  );

  // Pack.group[].highlightOnDashboard (admin/DB-set) - same rotating conic-gradient glow ring as
  // the "Download Analysis" button on the test analysis screen (TestAnalysis.jsx), just with this
  // app's gold palette (the challenge pill/sidebar tab) instead of that button's purple one. That
  // button's gradient was two similar mid-tone purples evenly split across the circle - fine on its
  // small pill shape, but on this much larger rectangular card the same two-tone split read as a
  // static border instead of a visibly moving glow (the gradual shift is too subtle over a longer
  // perimeter, and barely perceptible at only 1px thick). Switched to a dim gold base ring with one
  // bright highlight arc sweeping around it, and thickened the ring, so the motion actually reads.
  if (test.highlightOnDashboard) {
    return (
      <div className="relative rounded-xl p-[2px] md:h-[135px] h-[100px] w-full overflow-hidden">
        <span
          aria-hidden="true"
          className="c99h-hl-glow pointer-events-none absolute inset-[-1000%] animate-[spin_2.5s_linear_infinite]"
        />
        {/* hover:bg-[var(--q3-surface-dimmer)] looked fine on the plain card below, but that token
            is a near-transparent overlay in light mode (~6% alpha, meant to tint an opaque surface
            underneath) - swapping to it on hover let the spinning gold layer behind this div bleed
            straight through instead of staying covered. brightness-95 darkens the already-opaque
            rendered pixels instead, so it can't expose whatever's behind. */}
        <div
          className="relative h-full w-full rounded-xl shadow-none bg-[var(--q3-surface-dim)] flex flex-col justify-between p-3 pl-5 cursor-pointer hover:brightness-95 transition-all duration-300"
          onClick={handleCardClick}
        >
          {cardBody}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-xl md:h-[135px] h-[100px] w-full shadow-none bg-[var(--q3-surface-dim)] flex flex-col justify-between p-3 pl-5 cursor-pointer hover:bg-[var(--q3-surface-dimmer)] hover:border hover:border-1 transition-all duration-300`}
      onClick={handleCardClick}
    >
      {cardBody}
    </div>
  );
}
